import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './Suppliers.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SUPABASE_URL = 'https://yulypcgdgxzqtdkysywg.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1bHlwY2dkZ3h6cXRka3lzeXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMzM4NDcsImV4cCI6MjA5MjYwOTg0N30.jeMCN3jQaIzdZor8I_ZdKLg5pBx6iiPdGQdaV26RrUw';

const SUPPLIER_TYPES = ['Subcontractor', 'Material Supplier', 'Service Provider', 'Waste Contractor', 'H&S Consultant', 'Other'];

const STATUS_CONFIG = {
  'approved':         { label: 'Approved',       cls: 'sup-status-approved'  },
  'pending':          { label: 'Pending Review',  cls: 'sup-status-pending'   },
  'suspended':        { label: 'Suspended',       cls: 'sup-status-suspended' },
  'rejected':         { label: 'Rejected',        cls: 'sup-status-rejected'  },
};

const EMPTY_FORM = {
  name: '', type: 'Subcontractor', contact_name: '', contact_email: '',
  contact_phone: '', address: '', website: '', company_number: '', status: 'pending', performance_rating: '',
  approved_date: '', review_date: '',
  public_liability_expiry: '', public_liability_url: '',
  employers_liability_expiry: '', employers_liability_url: '',
  professional_indemnity_expiry: '', professional_indemnity_url: '',
  waste_carrier_licence_url: '', notes: '',
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(str) {
  if (!str) return null;
  return Math.ceil((new Date(str) - new Date()) / 86400000);
}

function getInsuranceStatus(expiry) {
  if (!expiry) return null;
  const days = daysUntil(expiry);
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring';
  return 'current';
}

function StarRating({ rating, onChange }) {
  return (
    <div className="sup-stars">
      {[1,2,3,4,5].map(n => (
        <span
          key={n}
          className={`sup-star ${rating >= n ? 'filled' : ''}`}
          onClick={() => onChange && onChange(n)}
        >★</span>
      ))}
    </div>
  );
}

function InsuranceBadge({ expiry, label }) {
  const status = getInsuranceStatus(expiry);
  if (!expiry) return <span className="ins-badge missing">{label}: Not held</span>;
  const days = daysUntil(expiry);
  return (
    <span className={`ins-badge ${status}`}>
      {label}: {days < 0 ? `Expired ${Math.abs(days)}d ago` : days <= 30 ? `Expires in ${days}d` : formatDate(expiry)}
    </span>
  );
}

export default function Suppliers({ user }) {
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'detail' | 'edit'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [uploadingFile, setUploadingFile] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [supRes, userRes] = await Promise.all([
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/users/all`),
      ]);
      setSuppliers(supRes.data);
      setUsers(userRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const counts = useMemo(() => ({
    total:     suppliers.filter(s => !s.is_archived).length,
    approved:  suppliers.filter(s => !s.is_archived && s.status === 'approved').length,
    pending:   suppliers.filter(s => !s.is_archived && s.status === 'pending').length,
    suspended: suppliers.filter(s => !s.is_archived && s.status === 'suspended').length,
    insIssues: suppliers.filter(s => !s.is_archived && (
      getInsuranceStatus(s.public_liability_expiry) === 'expired' ||
      getInsuranceStatus(s.public_liability_expiry) === 'expiring' ||
      getInsuranceStatus(s.employers_liability_expiry) === 'expired' ||
      getInsuranceStatus(s.employers_liability_expiry) === 'expiring'
    )).length,
  }), [suppliers]);

  const filtered = useMemo(() => {
    return suppliers
      .filter(s => !s.is_archived)
      .filter(s => typeFilter === 'All' || s.type === typeFilter)
      .filter(s => statusFilter === 'All' || s.status === statusFilter)
      .filter(s => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (s.name || '').toLowerCase().includes(q) ||
               (s.contact_name || '').toLowerCase().includes(q) ||
               (s.type || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [suppliers, typeFilter, statusFilter, search]);

  const [lookupNumber, setLookupNumber] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  const runLookup = async () => {
    if (!lookupNumber.trim()) return;
    setLookupLoading(true);
    setLookupResult(null);
    setLookupError('');
    try {
      const res = await axios.post(`${API}/analyse/supplier-lookup`, { company_number: lookupNumber });
      const data = res.data;
      setLookupResult(data);
      setForm(prev => ({
        ...prev,
        name: data.name || prev.name,
        type: data.suggested_type || prev.type,
        contact_name: data.contact_name || prev.contact_name,
        contact_email: data.contact_email || prev.contact_email,
        contact_phone: data.contact_phone || prev.contact_phone,
        address: data.address || prev.address,
        website: data.website || prev.website,
        company_number: data.company_number || prev.company_number,
        notes: data.description ? `${data.description}${data.sic_codes?.length ? ` SIC: ${data.sic_codes.join(', ')}` : ''}` : prev.notes,
      }));
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Lookup failed — please fill in manually.');
    } finally {
      setLookupLoading(false);
    }
  };

  const uploadCombinedInsurance = async (file) => {
    if (!file) return;
    setUploadingFile('combined_insurance');
    try {
      const ext = file.name.split('.').pop();
      const safeName = (form.name || 'supplier').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const path = `documents/suppliers/${safeName}_combined_insurance_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Content-Type': file.type,
          'x-upsert': 'true',
        },
        body: file,
      });
      if (res.ok) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        // Same URL for both PL and EL — one doc covers both
        setForm(prev => ({ ...prev, public_liability_url: url, employers_liability_url: url }));

        // Analyse to extract expiry dates
        if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
          try {
            const toBase64 = (f) => new Promise((res, rej) => {
              const r = new FileReader();
              r.onload = () => res(r.result.split(',')[1]);
              r.onerror = rej;
              r.readAsDataURL(f);
            });
            const base64 = await toBase64(file);
            const analyseRes = await axios.post(`${API}/analyse/insurance-cert-combined`, { base64, mediaType: file.type });
            const data = analyseRes.data;
            setForm(prev => ({
              ...prev,
              public_liability_expiry: data.pl_expiry || prev.public_liability_expiry,
              employers_liability_expiry: data.el_expiry || prev.employers_liability_expiry,
            }));
          } catch (e) { console.log('Combined insurance analysis failed:', e.message); }
        }
      }
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingFile(''); }
  };

  const uploadInsuranceDoc = async (file, field) => {
    if (!file) return;
    setUploadingFile(field);
    try {
      const ext = file.name.split('.').pop();
      const safeName = (form.name || 'supplier').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const path = `documents/suppliers/${safeName}_${field}_${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${SUPABASE_KEY}`,
          'Content-Type': file.type,
          'x-upsert': 'true',
        },
        body: file,
      });
      if (res.ok) {
        const url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
        setForm(prev => ({ ...prev, [field]: url }));

        // Try to extract expiry date from certificate
        if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
          const expiryFieldMap = {
            public_liability_url: 'public_liability_expiry',
            employers_liability_url: 'employers_liability_expiry',
            professional_indemnity_url: 'professional_indemnity_expiry',
          };
          const expiryField = expiryFieldMap[field];
          if (expiryField) {
            try {
              const toBase64 = (f) => new Promise((res, rej) => {
                const r = new FileReader();
                r.onload = () => res(r.result.split(',')[1]);
                r.onerror = rej;
                r.readAsDataURL(f);
              });
              const base64 = await toBase64(file);
              const analyseRes = await axios.post(`${API}/analyse/insurance-cert`, { base64, mediaType: file.type });
              if (analyseRes.data.expiry_date) {
                setForm(prev => ({ ...prev, [expiryField]: analyseRes.data.expiry_date }));
              }
            } catch (e) { console.log('Insurance cert analysis failed:', e.message); }
          }
        }
      }
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploadingFile(''); }
  };

  const handleSave = async () => {
    if (!form.name) { alert('Supplier name is required.'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        performance_rating: form.performance_rating ? parseInt(form.performance_rating) : null,
        approved_date: form.approved_date || null,
        review_date: form.review_date || null,
        public_liability_expiry: form.public_liability_expiry || null,
        employers_liability_expiry: form.employers_liability_expiry || null,
        professional_indemnity_expiry: form.professional_indemnity_expiry || null,
        approved: form.status === 'approved',
        approved_by: form.status === 'approved' ? user.id : null,
        created_by: user.id,
      };

      if (selected && view === 'edit') {
        await axios.patch(`${API}/suppliers/${selected.id}`, { ...payload, updated_by: user.id });
      } else {
        await axios.post(`${API}/suppliers`, payload);
      }

      setView('list');
      setForm(EMPTY_FORM);
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error saving: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleApprove = async (supplier) => {
    try {
      await axios.patch(`${API}/suppliers/${supplier.id}`, {
        status: 'approved', approved: true,
        approved_by: user.id,
        approved_date: new Date().toISOString().slice(0, 10),
        updated_by: user.id,
      });
      fetchAll();
      if (selected?.id === supplier.id) setSelected({ ...selected, status: 'approved' });
    } catch (err) { alert('Error approving supplier.'); }
  };

  const handleArchive = async (supplier) => {
    if (!window.confirm(`Archive ${supplier.name}? It will be hidden but not deleted.`)) return;
    try {
      await axios.patch(`${API}/suppliers/${supplier.id}`, {
        is_archived: true,
        archived_at: new Date().toISOString(),
        archived_by: user.id,
      });
      setView('list');
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error archiving supplier.'); }
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(`Permanently delete ${supplier.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/suppliers/${supplier.id}`);
      setView('list');
      setSelected(null);
      fetchAll();
    } catch (err) { alert('Error deleting supplier.'); }
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || '', type: s.type || 'Subcontractor',
      contact_name: s.contact_name || '', contact_email: s.contact_email || '',
      contact_phone: s.contact_phone || '', address: s.address || '',
      website: s.website || '',
      company_number: s.company_number || '',
      status: s.status || 'pending', performance_rating: s.performance_rating || '',
      approved_date: s.approved_date || '', review_date: s.review_date || '',
      public_liability_expiry: s.public_liability_expiry || '',
      public_liability_url: s.public_liability_url || '',
      employers_liability_expiry: s.employers_liability_expiry || '',
      employers_liability_url: s.employers_liability_url || '',
      professional_indemnity_expiry: s.professional_indemnity_expiry || '',
      professional_indemnity_url: s.professional_indemnity_url || '',
      waste_carrier_licence_url: s.waste_carrier_licence_url || '',
      notes: s.notes || '',
    });
    setView('edit');
  };

  const userName = (id) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.first_name} ${u.last_name}` : '—';
  };

  if (loading) return <div className="sup-loading">Loading suppliers...</div>;

  // ── FORM (add or edit) ───────────────────────────────────────
  if (view === 'add' || view === 'edit') return (
    <div className="sup-form-page">
      <button className="sup-back" onClick={() => { setView(selected ? 'detail' : 'list'); }}>
        ← {selected ? selected.name.toUpperCase() : 'SUPPLIERS'}
      </button>
      <div className="sup-form-header">
        <h2>{view === 'edit' ? 'EDIT SUPPLIER' : 'ADD SUPPLIER'}</h2>
      </div>

      <div className="sup-form">
        {/* Companies House Lookup */}
        <div className="sup-lookup-bar full">
          <div className="sup-lookup-label">COMPANIES HOUSE LOOKUP</div>
          <div className="sup-lookup-row">
            <input
              className="sup-lookup-input"
              value={lookupNumber}
              onChange={e => setLookupNumber(e.target.value)}
              placeholder="Enter company number e.g. 09422349"
              onKeyDown={e => e.key === 'Enter' && runLookup()}
            />
            <button className={`sup-lookup-btn ${lookupLoading ? 'loading' : ''}`} onClick={runLookup} disabled={lookupLoading || !lookupNumber.trim()}>
              {lookupLoading ? <><span className="sup-lookup-spinner" />Looking up...</> : '✦ Lookup'}
            </button>
          </div>
          {lookupError && <div className="sup-lookup-error">{lookupError}</div>}
          {lookupResult && (
            <div className="sup-lookup-result">
              <span className="sup-lookup-found">✓ Found: <strong>{lookupResult.name}</strong></span>
              <span className="sup-lookup-status">{lookupResult.status}</span>
              {lookupResult.website && <a href={lookupResult.website} target="_blank" rel="noreferrer" className="sup-lookup-web">Website ↗</a>}
              <span className="sup-lookup-hint">Fields pre-filled below — check and adjust as needed</span>
            </div>
          )}
        </div>

        <div className="sup-form-section-title">SUPPLIER DETAILS</div>
        <div className="sup-field full">
          <label>SUPPLIER NAME *</label>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. F&R Cawleys" />
        </div>
        <div className="sup-field">
          <label>TYPE</label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            {SUPPLIER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="sup-field">
          <label>STATUS</label>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="suspended">Suspended</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div className="sup-field">
          <label>PERFORMANCE RATING</label>
          <StarRating rating={parseInt(form.performance_rating) || 0} onChange={n => setForm({ ...form, performance_rating: n })} />
        </div>
        <div className="sup-field">
          <label>REVIEW DATE</label>
          <input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} />
        </div>

        <div className="sup-form-section-title">CONTACT</div>
        <div className="sup-field">
          <label>CONTACT NAME</label>
          <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="Contact person" />
        </div>
        <div className="sup-field">
          <label>EMAIL</label>
          <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} placeholder="email@example.com" />
        </div>
        <div className="sup-field">
          <label>PHONE</label>
          <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="01234 567890" />
        </div>
        <div className="sup-field">
          <label>COMPANIES HOUSE NUMBER</label>
          <input value={form.company_number} onChange={e => setForm({ ...form, company_number: e.target.value })} placeholder="e.g. 09422349" />
        </div>
        <div className="sup-field">
          <label>WEBSITE</label>
          <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://www.example.com" />
        </div>
        <div className="sup-field">
          <label>ADDRESS</label>
          <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" />
        </div>

        <div className="sup-form-section-title">INSURANCE</div>
        
        {/* Combined insurance certificate */}
        <div className="sup-insurance-combined">
          <div className="sup-field full">
            <label>COMBINED INSURANCE CERTIFICATE (PUBLIC LIABILITY & EMPLOYERS LIABILITY)</label>
            <div className="sup-file-row">
              {form.public_liability_url && <a href={form.public_liability_url} target="_blank" rel="noreferrer" className="sup-view-link">View ↗</a>}
              {form.public_liability_url && (
                <button className="sup-remove-btn" onClick={() => setForm({ ...form, public_liability_url: '', employers_liability_url: '', public_liability_expiry: '', employers_liability_expiry: '' })} title="Remove certificate">✕</button>
              )}
              <label className="sup-upload-btn">
                {uploadingFile === 'combined_insurance' ? 'Uploading & analysing...' : form.public_liability_url ? 'Replace' : 'Upload'}
                <input type="file" accept=".pdf,.jpg,.png" style={{ display: 'none' }} onChange={e => uploadCombinedInsurance(e.target.files[0])} />
              </label>
            </div>
            {uploadingFile === 'combined_insurance' && <div className="sup-analysing">Reading certificate — Claude is extracting expiry dates...</div>}
          </div>
          <div className="sup-insurance-row">
            <div className="sup-field">
              <label>PUBLIC LIABILITY EXPIRY</label>
              <input type="date" value={form.public_liability_expiry} onChange={e => setForm({ ...form, public_liability_expiry: e.target.value })} />
            </div>
            <div className="sup-field">
              <label>EMPLOYERS LIABILITY EXPIRY</label>
              <input type="date" value={form.employers_liability_expiry} onChange={e => setForm({ ...form, employers_liability_expiry: e.target.value })} />
            </div>
          </div>
        </div>

        {/* PI insurance — separate */}
        <div className="sup-insurance-row">
          <div className="sup-field">
            <label>PROFESSIONAL INDEMNITY — EXPIRY DATE</label>
            <input type="date" value={form.professional_indemnity_expiry} onChange={e => setForm({ ...form, professional_indemnity_expiry: e.target.value })} />
          </div>
          <div className="sup-field">
            <label>PI CERTIFICATE</label>
            <div className="sup-file-row">
              {form.professional_indemnity_url && <a href={form.professional_indemnity_url} target="_blank" rel="noreferrer" className="sup-view-link">View ↗</a>}
              {form.professional_indemnity_url && (
                <button className="sup-remove-btn" onClick={() => setForm({ ...form, professional_indemnity_url: '', professional_indemnity_expiry: '' })} title="Remove certificate">✕</button>
              )}
              <label className="sup-upload-btn">
                {uploadingFile === 'professional_indemnity_url' ? 'Uploading...' : form.professional_indemnity_url ? 'Replace' : 'Upload'}
                <input type="file" accept=".pdf,.jpg,.png" style={{ display: 'none' }} onChange={e => uploadInsuranceDoc(e.target.files[0], 'professional_indemnity_url')} />
              </label>
            </div>
          </div>
        </div>

        <div className="sup-form-section-title">OTHER</div>
        <div className="sup-field">
          <label>WASTE CARRIER LICENCE</label>
          <div className="sup-file-row">
            {form.waste_carrier_licence_url && <a href={form.waste_carrier_licence_url} target="_blank" rel="noreferrer" className="sup-view-link">View ↗</a>}
            {form.waste_carrier_licence_url && (
              <button className="sup-remove-btn" onClick={() => setForm({ ...form, waste_carrier_licence_url: '' })} title="Remove licence">✕</button>
            )}
            <label className="sup-upload-btn">
              {uploadingFile === 'waste_carrier_licence_url' ? 'Uploading...' : form.waste_carrier_licence_url ? 'Replace' : 'Upload'}
              <input type="file" accept=".pdf,.jpg,.png" style={{ display: 'none' }} onChange={e => uploadInsuranceDoc(e.target.files[0], 'waste_carrier_licence_url')} />
            </label>
          </div>
        </div>
        <div className="sup-field full">
          <label>NOTES</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
        </div>

        <div className="sup-form-actions">
          <button className="sup-btn secondary" onClick={() => { setView(selected ? 'detail' : 'list'); }}>CANCEL</button>
          <button className={`sup-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? 'SAVING...' : view === 'edit' ? 'SAVE CHANGES' : 'ADD SUPPLIER'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const status = STATUS_CONFIG[selected.status] || STATUS_CONFIG['pending'];
    return (
      <div className="sup-detail-page">
        <button className="sup-back" onClick={() => { setView('list'); setSelected(null); }}>← SUPPLIERS</button>

        <div className="sup-detail-header">
          <div>
            <h2 className="sup-detail-name">{selected.name}</h2>
            <div className="sup-detail-meta">
              <span className="sup-type-badge">{selected.type}</span>
              <span className={`sup-status-badge ${status.cls}`}>{status.label}</span>
              {selected.performance_rating && <StarRating rating={selected.performance_rating} />}
            </div>
          </div>
          {user.role === 'admin' && (
            <div className="sup-detail-header-actions">
              {selected.status !== 'approved' && (
                <button className="sup-btn approve" onClick={() => handleApprove(selected)}>✓ APPROVE</button>
              )}
              <button className="sup-btn secondary" onClick={() => openEdit(selected)}>EDIT</button>
              <button className="sup-btn archive" onClick={() => handleArchive(selected)}>ARCHIVE</button>
              <button className="sup-btn danger" onClick={() => handleDelete(selected)}>DELETE</button>
            </div>
          )}
        </div>

        <div className="sup-detail-grid">
          <div className="sup-detail-col">
            <div className="sup-detail-block">
              <div className="sup-block-title">CONTACT DETAILS</div>
              {selected.contact_name && <div className="sup-block-row"><span>Contact</span><span>{selected.contact_name}</span></div>}
              {selected.company_number && <div className="sup-block-row"><span>Co. Number</span><a href={`https://find-and-update.company-information.service.gov.uk/company/${selected.company_number}`} target="_blank" rel="noreferrer" className="sup-link">{selected.company_number}</a></div>}
              {selected.contact_email && <div className="sup-block-row"><span>Email</span><a href={`mailto:${selected.contact_email}`} className="sup-link">{selected.contact_email}</a></div>}
              {selected.contact_phone && <div className="sup-block-row"><span>Phone</span><span>{selected.contact_phone}</span></div>}
              {selected.website && <div className="sup-block-row"><span>Website</span><a href={selected.website} target="_blank" rel="noreferrer" className="sup-link">{selected.website}</a></div>}
              {selected.address && <div className="sup-block-row"><span>Address</span><span>{selected.address}</span></div>}
            </div>

            <div className="sup-detail-block">
              <div className="sup-block-title">APPROVAL</div>
              <div className="sup-block-row"><span>Status</span><span className={`sup-status-badge ${status.cls}`}>{status.label}</span></div>
              {selected.approved_date && <div className="sup-block-row"><span>Approved on</span><span>{formatDate(selected.approved_date)}</span></div>}
              {selected.approved_by && <div className="sup-block-row"><span>Approved by</span><span>{userName(selected.approved_by)}</span></div>}
              {selected.review_date && <div className="sup-block-row"><span>Next review</span><span>{formatDate(selected.review_date)}</span></div>}
            </div>

            {selected.notes && (
              <div className="sup-detail-block">
                <div className="sup-block-title">NOTES</div>
                <p className="sup-block-text">{selected.notes}</p>
              </div>
            )}
          </div>

          <div className="sup-detail-col">
            <div className="sup-detail-block">
              <div className="sup-block-title">INSURANCE</div>
              {[
                { label: 'Public Liability', expiry: selected.public_liability_expiry, url: selected.public_liability_url },
                { label: 'Employers Liability', expiry: selected.employers_liability_expiry, url: selected.employers_liability_url },
                { label: 'Professional Indemnity', expiry: selected.professional_indemnity_expiry, url: selected.professional_indemnity_url },
              ].map(ins => {
                const insStatus = getInsuranceStatus(ins.expiry);
                return (
                  <div key={ins.label} className="sup-ins-row">
                    <div className="sup-ins-info">
                      <span className="sup-ins-label">{ins.label}</span>
                      {ins.expiry ? (
                        <span className={`sup-ins-expiry ${insStatus}`}>
                          {insStatus === 'expired' ? `Expired ${formatDate(ins.expiry)}` :
                           insStatus === 'expiring' ? `Expiring ${formatDate(ins.expiry)}` :
                           formatDate(ins.expiry)}
                        </span>
                      ) : (
                        <span className="sup-ins-expiry missing">Not recorded</span>
                      )}
                    </div>
                    {ins.url && <a href={ins.url} target="_blank" rel="noreferrer" className="sup-view-link">View ↗</a>}
                  </div>
                );
              })}
              {selected.waste_carrier_licence_url && (
                <div className="sup-ins-row">
                  <div className="sup-ins-info">
                    <span className="sup-ins-label">Waste Carrier Licence</span>
                  </div>
                  <a href={selected.waste_carrier_licence_url} target="_blank" rel="noreferrer" className="sup-view-link">View ↗</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className="sup-page">

      {/* Insurance alert */}
      {counts.insIssues > 0 && (
        <div className="sup-alert">
          ⚠ {counts.insIssues} supplier{counts.insIssues > 1 ? 's have' : ' has'} insurance expiring soon or expired
        </div>
      )}

      {/* Stat cards */}
      <div className="sup-stats">
        <div className="sup-stat" onClick={() => setStatusFilter('All')} style={{ cursor: 'pointer' }}>
          <span className="sup-stat-value">{counts.total}</span>
          <span className="sup-stat-label">Total</span>
        </div>
        <div className="sup-stat approved" onClick={() => setStatusFilter('approved')} style={{ cursor: 'pointer' }}>
          <span className="sup-stat-value">{counts.approved}</span>
          <span className="sup-stat-label">Approved</span>
        </div>
        <div className="sup-stat pending" onClick={() => setStatusFilter('pending')} style={{ cursor: 'pointer' }}>
          <span className="sup-stat-value">{counts.pending}</span>
          <span className="sup-stat-label">Pending</span>
        </div>
        <div className="sup-stat suspended" onClick={() => setStatusFilter('suspended')} style={{ cursor: 'pointer' }}>
          <span className="sup-stat-value">{counts.suspended}</span>
          <span className="sup-stat-label">Suspended</span>
        </div>
      </div>

      {/* Controls */}
      <div className="sup-controls">
        <div className="sup-search">
          <input type="text" placeholder="Search suppliers..." value={search} onChange={e => setSearch(e.target.value)} className="sup-search-input" />
        </div>
        <div className="sup-filter-group">
          {['All', ...SUPPLIER_TYPES].map(t => (
            <button key={t} className={`sup-filter-btn ${typeFilter === t ? 'active' : ''}`} onClick={() => setTypeFilter(t)}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
        {user.role === 'admin' && (
          <button className="sup-add-btn" onClick={() => { setForm(EMPTY_FORM); setSelected(null); setView('add'); }}>+ ADD SUPPLIER</button>
        )}
      </div>

      <div className="sup-count">{filtered.length} supplier{filtered.length !== 1 ? 's' : ''}</div>

      {suppliers.length === 0 ? (
        <div className="sup-empty">
          <div className="sup-empty-icon">🏢</div>
          <p>No suppliers added yet.</p>
          {user.role === 'admin' && <button className="sup-btn primary" onClick={() => { setForm(EMPTY_FORM); setView('add'); }}>Add your first supplier</button>}
        </div>
      ) : filtered.length === 0 ? (
        <div className="sup-empty"><p>No suppliers match your filters.</p></div>
      ) : (
        <div className="sup-list">
          {filtered.map(s => {
            const status = STATUS_CONFIG[s.status] || STATUS_CONFIG['pending'];
            const plStatus = getInsuranceStatus(s.public_liability_expiry);
            const elStatus = getInsuranceStatus(s.employers_liability_expiry);
            const hasInsIssue = plStatus === 'expired' || plStatus === 'expiring' || elStatus === 'expired' || elStatus === 'expiring';
            return (
              <div key={s.id} className={`sup-card ${hasInsIssue ? 'has-issue' : ''}`} onClick={() => { setSelected(s); setView('detail'); }}>
                <div className="sup-card-left">
                  <div className="sup-card-name">{s.name}</div>
                  <div className="sup-card-meta">
                    <span className="sup-type-badge">{s.type}</span>
                    {s.contact_name && <span>{s.contact_name}</span>}
                    {s.review_date && <span>Review: {formatDate(s.review_date)}</span>}
                  </div>
                  <div className="sup-card-insurance">
                    <InsuranceBadge expiry={s.public_liability_expiry} label="PL" />
                    <InsuranceBadge expiry={s.employers_liability_expiry} label="EL" />
                  </div>
                </div>
                <div className="sup-card-right">
                  <span className={`sup-status-badge ${status.cls}`}>{status.label}</span>
                  {s.performance_rating && <StarRating rating={s.performance_rating} />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
